import { useCallback, useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { orderBy, limit } from 'firebase/firestore';
import type { Project, Regeneration } from '@allemnii-pulse/shared';
import { useCollection } from '../../lib/firestore.js';
import { useOrgProjects } from '../../lib/useOrgProjects.js';
import { Card } from '../../components/ui/Card.js';

const LATEST_REGENERATION = [orderBy('createdAt', 'desc'), limit(1)];

/** Renders nothing — just watches one project's most recent regeneration and reports whether it
 * still needs review, so the parent can aggregate a dynamic-length list of projects without
 * breaking the rules of hooks. A project is flagged when its latest regeneration exists and
 * hasn't reached 'completed' — see the API's regenerations.ts, which now sets that status once
 * every proposal has been decided (or immediately, if a regeneration was created with none). */
function RegenFlagProbe({
  project,
  onResult,
}: {
  project: Project;
  onResult: (projectId: string, flagged: boolean) => void;
}) {
  const { data: regenerations, loading } = useCollection<Regeneration>(
    `projects/${project.id}/regenerations`,
    LATEST_REGENERATION,
  );
  const latest = regenerations[0];
  const flagged = latest ? latest.status !== 'completed' : false;

  useEffect(() => {
    if (!loading) onResult(project.id, flagged);
  }, [loading, flagged, project.id, onResult]);

  return null;
}

/** Org-wide view of projects with an outstanding regeneration review, so a stale task set
 * doesn't get worked on unnoticed. Links into each project's regeneration review. */
export function RegenerationFlagsWidget() {

  const { data: projects, loading: projectsLoading } = useOrgProjects();
  const activeProjects = useMemo(() => projects.filter((p) => p.status === 'active'), [projects]);

  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const handleResult = useCallback((projectId: string, flagged: boolean) => {
    setFlags((prev) => (prev[projectId] === flagged ? prev : { ...prev, [projectId]: flagged }));
  }, []);

  const allProbed = activeProjects.every((p) => p.id in flags);
  const flaggedProjects = activeProjects.filter((p) => flags[p.id]);

  return (
    <Card className="flex flex-col overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border p-3">
        <span className="text-xs font-medium">Regeneration impact</span>
        {flaggedProjects.length > 0 && (
          <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-medium text-accent-soft-fg">
            {flaggedProjects.length}
          </span>
        )}
      </div>

      {projectsLoading || !allProbed ? (
        <p className="p-4 text-sm text-muted">Loading…</p>
      ) : flaggedProjects.length === 0 ? (
        <p className="p-8 text-center text-sm text-muted">Nothing pending regeneration review.</p>
      ) : (
        <div className="flex flex-col divide-y divide-border">
          {flaggedProjects.map((p) => (
            <Link
              key={p.id}
              to={`/projects/${p.id}/regenerations`}
              className="flex items-center gap-3 p-3 transition-colors hover:bg-surface-2"
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
              <span className="flex-1 truncate text-sm">{p.name}</span>
              <span className="text-xs text-accent">review impact →</span>
            </Link>
          ))}
        </div>
      )}

      {activeProjects.map((p) => (
        <RegenFlagProbe key={p.id} project={p} onResult={handleResult} />
      ))}
    </Card>
  );
}
